// The master texting switch. Henry, 2026-09-23: "we need to remove texting from
// the system for the time."
//
// ONE owner-only flag, appSettings/texting { enabled }, turns texting off in two
// places at once:
//   - here, in the browser, because every texting surface in the CRM hangs off
//     canUseTexting() (the sidebar link, the Texting Center route, the deal
//     Texts tab, the Home card, the lead drawer, every unread badge);
//   - on the server, where functions-api reads the same doc before it accepts an
//     inbound text, sends one, or runs either of the texting jobs.
// Because both sides read the doc live, flipping the switch takes effect
// straight away. No build, no upload, no deploy.
//
// NOTHING IS DELETED while texting is off. Conversations, messages, each agent's
// RingCentral connection and the automated-text settings stay exactly as they
// are, so switching it back on brings the feature back as it was.
//
// Unknown is never off. A missing doc, a missing field, a failed read or a
// browser with storage blocked all leave texting ON, which is how the app
// behaved before this file existed. Only a deliberate `enabled: false` turns it
// off, so a hiccup reading one document can never take a live feature down.
import { doc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { setDoc } from "./lookOnlyFirestore";
import { assertCanWrite } from "./lookOnly";

export const TEXTING_FLAG_DOC = ["appSettings", "texting"];
const REMEMBERED = "te.texting.enabled";

const listeners = new Set();
let enabled = remembered();

// The last answer this browser saw, so a reload does not flash the texting menu
// on screen for a moment before the flag arrives. Storage can be blocked or
// cleared; then we simply start from "on" as before.
function remembered() {
  try {
    return localStorage.getItem(REMEMBERED) !== "off";
  } catch (e) {
    return true;
  }
}

function remember(value) {
  try {
    localStorage.setItem(REMEMBERED, value ? "on" : "off");
  } catch (e) {
    /* private window, blocked storage: the flag still works for this visit */
  }
}

/** Is texting switched on? Unknown counts as on. */
export function isTextingEnabled() {
  return enabled !== false;
}

/** Apply an answer (from the server, or a test). Only a real false turns it off. */
export function applyTextingFlag(value) {
  const next = value === false;
  if (enabled === !next) {
    remember(!next);
    return;
  }
  enabled = !next;
  remember(enabled);
  listeners.forEach((fn) => {
    try {
      fn(enabled);
    } catch (e) {
      /* one broken listener must not stop the others */
    }
  });
}

/** Tell me when the switch flips. Returns the unsubscribe. */
export function subscribeTextingFlag(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Watch the flag doc. Call this only once signed in: every rule in
 * firestore.rules needs a signed-in staff member, and a read before sign-in is
 * the exact shape that hung the login screen in August.
 */
export function watchTextingFlag() {
  try {
    return onSnapshot(
      doc(firestored, ...TEXTING_FLAG_DOC),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        applyTextingFlag(data ? data.enabled : true);
      },
      (err) => {
        // A denied or failed read leaves texting as it was. Never switch a live
        // feature off because one document could not be read.
        console.error("texting switch read failed:", err);
      },
    );
  } catch (e) {
    console.error("texting switch listen failed:", e);
    return () => {};
  }
}

/** Owner-only write, from the Super Admin switch. */
export async function setTextingEnabled(nextEnabled, byUid) {
  assertCanWrite();
  const value = nextEnabled !== false;
  await setDoc(
    doc(firestored, ...TEXTING_FLAG_DOC),
    { enabled: value, updatedAt: serverTimestamp(), updatedBy: String(byUid || "") },
    { merge: true },
  );
  applyTextingFlag(value);
  return value;
}
